import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatMoneyWithCurrency, formatRut } from "src/utils/formatter";
import {
  buildCobranzaGestionHistoryItems,
  getCobranzaEmailHistoryFacts,
  getCobranzaEmailHistoryInvoices,
} from "./cobranzaGestionUtils";
import CobranzaEmailHistoryPreviewButton from "./CobranzaEmailHistoryPreviewButton";
import CobranzaEmailRecipientsStatus from "./CobranzaEmailRecipientsStatus";
import "src/components/repositorio/HistoryList.styles.scss";

const ACTION_COLOR = "#176f80";

function getEventIcon(eventType) {
  const t = String(eventType || "").toUpperCase();
  if (t === "EMAIL_SENT") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22L11 13L2 9L22 2z" />
      </svg>
    );
  }
  if (t === "ASSIGNEE_CHANGED") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    );
  }
  if (t === "DOCUMENTS_SYNCED") {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function translateEventVerb(eventType) {
  const t = String(eventType || "").toUpperCase();
  const map = {
    EMAIL_SENT: "envió",
    ASSIGNEE_CHANGED: "actualizó",
    DECISION_CHANGED: "cambió",
    CHANNEL_CHANGED: "cambió",
    RESPONSE_UPDATED: "registró",
    NEXT_STEPS_UPDATED: "registró",
    CLIENT_STATUS_UPDATED: "actualizó",
    BUSINESS_LINE_UPDATED: "actualizó",
    BUSINESS_GROUP_UPDATED: "actualizó",
    DOCUMENTS_SYNCED: "aplicó",
  };
  return map[t] || "registró";
}

function translateEventObject(eventType) {
  const t = String(eventType || "").toUpperCase();
  const map = {
    EMAIL_SENT: "un correo de cobranza",
    ASSIGNEE_CHANGED: "el responsable",
    DECISION_CHANGED: "la decisión de cobro",
    CHANNEL_CHANGED: "el canal de gestión",
    RESPONSE_UPDATED: "la respuesta de contacto",
    NEXT_STEPS_UPDATED: "los próximos pasos",
    CLIENT_STATUS_UPDATED: "el estado del cliente",
    BUSINESS_LINE_UPDATED: "la línea de negocio",
    BUSINESS_GROUP_UPDATED: "el grupo empresarial",
    DOCUMENTS_SYNCED: "gestión en las facturas",
  };
  return map[t] || "un evento de gestión";
}

export default function CobranzaGestionHistoryList({ row, agenda, clientDocs = [] }) {
  const { t } = useTranslation("cobranza");
  const sortedHistory = useMemo(
    () => buildCobranzaGestionHistoryItems(agenda, t),
    [agenda, t]
  );

  const openCount = (clientDocs || []).filter((d) => Number(d.remaining || 0) > 0).length;
  const totalSaldo = (clientDocs || []).reduce(
    (sum, d) => sum + Number(d.remaining || 0),
    0
  );

  if (sortedHistory.length === 0) {
    return (
      <div className="history-list-empty">
        <p>{t("gestionHistory.noHistory")}</p>
      </div>
    );
  }

  const renderEmailDetails = (emailLog) => {
    const facts = getCobranzaEmailHistoryFacts(emailLog, t);
    const invoices = getCobranzaEmailHistoryInvoices(emailLog, t);

    return (
      <div className="cf-gestion-email-history">
        <div className="cf-gestion-email-history__subject-row cf-gestion-email-history__subject-row--compact">
          <CobranzaEmailRecipientsStatus entry={emailLog} />
        </div>
        {facts.length > 0 && (
          <div className="cf-gestion-email-history__facts">
            {facts.map((fact) => (
              <span key={`${fact.label}-${fact.value}`} className="cf-gestion-email-history__fact">
                <strong>{fact.label}:</strong> {fact.value}
              </span>
            ))}
          </div>
        )}
        <div className="cf-gestion-email-history__actions">
          <CobranzaEmailHistoryPreviewButton entry={emailLog} />
        </div>
        {invoices.length > 0 && (
          <ul className="cf-gestion-email-history__invoice-list">
            {invoices.map((invoice, index) => (
              <li
                key={`${invoice.documentId || invoice.label}-${index}`}
                className="cf-gestion-email-history__invoice-item"
              >
                <span className="cf-gestion-email-history__invoice-label">{invoice.label}</span>
                <span className="cf-gestion-email-history__invoice-meta">
                  Vence {invoice.dueDateLabel}
                  {invoice.amountLabel ? ` · ${invoice.amountLabel}` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="history-list-container">
      <div className="history-list-period-header">
        <div className="history-list-period-header-inner">
          <div className="history-list-period-info-row">
            <div className="history-list-period-left-group">
              <div className="history-list-period-title text-uppercase">
                {row?.issuerName || "Cliente"}
              </div>
              <span className="history-list-period-separator">|</span>
              <span className="history-list-period-count">
                {openCount} {openCount === 1 ? "factura" : "facturas"}
              </span>
            </div>
            <div className="history-list-period-right-group">
              <span className="history-list-period-status history-list-period-status--pending">
                <svg
                  className="history-list-status-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                {formatMoneyWithCurrency(totalSaldo, "CLP")}
              </span>
            </div>
          </div>
          {row?.issuerRut && (
            <div className="cf-gestion-history-rut">{formatRut(String(row.issuerRut))}</div>
          )}
        </div>
      </div>

      <div className="history-list-items">
        <div
          className={`history-list-items-inner ${
            sortedHistory.length >= 2 ? "history-list-items-inner--multiple" : ""
          }`}
        >
          {sortedHistory.map((item, index) => (
            <div key={item.id || index} className="history-list-row">
              <div className="history-list-item">
                <div
                  className="history-list-avatar"
                  style={{ backgroundColor: ACTION_COLOR }}
                >
                  {getEventIcon(item.eventType)}
                </div>
                <div className="history-list-body">
                  <div className="history-list-header">
                    <span className="history-list-user">{item.userEmail}</span>
                    <span className="history-list-action-verb">
                      {translateEventVerb(item.eventType)}
                    </span>
                    <span className="history-list-timeline-label">
                      {translateEventObject(item.eventType)}
                    </span>
                    <span className="history-list-time">
                      {item.dateLabel} a las {item.timeLabel}
                    </span>
                  </div>
                  {item.details && (
                    <div className="history-list-text">{item.details}</div>
                  )}
                  {item.eventType === "EMAIL_SENT" && item.emailLog && renderEmailDetails(item.emailLog)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
